import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FacetLabels } from '../../models/facet-labels.model';
import { TranslationService } from '../../services/translation.service';
import { DEFAULT_VISIBLE_FACET_VALUES, FACET_INCREMENT } from '../../constants/solr.constants';
import { getFacetLabel } from '../../utils/facet-label.util';
import { CapitalizePipe } from '../../pipes/capitalize.pipe';

interface FacetItem {
  value: string;
  count: number;
  label: string;
  forceVisible?: boolean;
}

const SEARCHABLE_FACET_KEYS = new Set(['domainId', 'categoryId', 'countyId', 'cityId']);

@Component({
  selector: 'app-facet-section',
  standalone: true,
  imports: [CommonModule, FormsModule, CapitalizePipe],
  templateUrl: './facet-section.component.html'
})
export class FacetSectionComponent implements OnChanges {
  @Input() facetKey!: string;
  @Input() title = '';
  @Input() values: Record<string, number> = {};
  @Input() selectedValues: string[] = [];
  @Input() allFacetLabels!: FacetLabels;
  @Input() showSearch = false;

  @Output() toggle = new EventEmitter<string>();

  filterText = '';
  isCollapsed = true;
  visibleCount = DEFAULT_VISIBLE_FACET_VALUES;

  items: FacetItem[] = [];
  visibleItems: FacetItem[] = [];
  hasFilterText = false;
  hasAvailableOptions = false;

  constructor(private readonly translation: TranslationService) {}

  get shouldShowSearchInput(): boolean {
    return this.showSearch && SEARCHABLE_FACET_KEYS.has(this.facetKey);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['values'] || changes['selectedValues'] || changes['allFacetLabels']) {
      this.rebuildItems();
    }
  }

  get searchPlaceholder(): string {
    return this.translation.translate('facetSearchPlaceholder');
  }

  toggleSection(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  onFilterInput(value: string): void {
    this.filterText = value;
    this.rebuildItems();
  }

  onToggleValue(value: string): void {
    this.toggle.emit(value);
  }

  showMore(): void {
    this.visibleCount = Math.min(this.visibleCount + FACET_INCREMENT, this.items.length);
    this.updateVisibleItems();
  }

  showLess(): void {
    const selectionCount = Array.isArray(this.selectedValues) ? this.selectedValues.length : 0;
    const minimumVisible = Math.max(
      Math.min(DEFAULT_VISIBLE_FACET_VALUES, this.items.length),
      selectionCount
    );

    this.visibleCount = minimumVisible;
    this.updateVisibleItems();
  }

  translate(key: string): string {
    return this.translation.translate(key);
  }

  get toggleLabel(): string {
    return this.isCollapsed ? this.translate('expandFacet') : this.translate('collapseFacet');
  }

  private rebuildItems(): void {
    const map = new Map<string, FacetItem>();

    Object.entries(this.values || {}).forEach(([value, count]) => {
      const normalized = String(value);
      map.set(normalized, {
        value: normalized,
        count: typeof count === 'number' ? count : Number(count) || 0,
        label: getFacetLabel(this.facetKey, normalized, this.allFacetLabels)
      });
    });

    (this.selectedValues || []).forEach(value => {
      const normalized = String(value);
      if (!map.has(normalized)) {
        map.set(normalized, {
          value: normalized,
          count: 0,
          label: getFacetLabel(this.facetKey, normalized, this.allFacetLabels),
          forceVisible: true
        });
      }
    });

    let items = Array.from(map.values());
    this.hasAvailableOptions = items.length > 0;

    this.hasFilterText = Boolean(this.filterText.trim());
    if (this.hasFilterText) {
      const term = this.filterText.trim().toLowerCase();
      items = items.filter(item => item.label.toLowerCase().includes(term));
    }

    const locale = this.translation.getLanguage() === 'ro' ? 'ro-RO' : 'en-US';
    items.sort((a, b) => {
      const labelA = a.label || a.value;
      const labelB = b.label || b.value;
      return labelA.localeCompare(labelB, locale, { sensitivity: 'base' });
    });

    this.items = items;

    const totalItems = this.items.length;
    const selectionCount = Array.isArray(this.selectedValues) ? this.selectedValues.length : 0;

    if (this.hasFilterText) {
      this.visibleCount = totalItems;
    } else {
      const baseVisible = Math.min(DEFAULT_VISIBLE_FACET_VALUES, totalItems);
      const requiredVisible = Math.max(baseVisible, selectionCount);

      if (this.visibleCount === 0) {
        this.visibleCount = requiredVisible;
      } else {
        this.visibleCount = Math.min(Math.max(this.visibleCount, requiredVisible), totalItems);
      }
    }

    this.updateVisibleItems();
  }

  private updateVisibleItems(): void {
    if (this.hasFilterText) {
      this.visibleItems = this.items;
      return;
    }

    const baseVisible = this.items.slice(0, this.visibleCount);
    const forcedVisible = this.items.filter(
      item => item.forceVisible && !baseVisible.some(existing => existing.value === item.value)
    );

    this.visibleItems = [...baseVisible, ...forcedVisible];
  }

  get canShowMore(): boolean {
    return !this.hasFilterText && this.visibleCount < this.items.length;
  }

  get canShowLess(): boolean {
    const selectionCount = Array.isArray(this.selectedValues) ? this.selectedValues.length : 0;
    const minimumVisible = Math.max(
      Math.min(DEFAULT_VISIBLE_FACET_VALUES, this.items.length),
      selectionCount
    );

    return !this.hasFilterText && this.visibleCount > minimumVisible;
  }
}
